import { Component } from '@theme/component';

class BVPremiumMotion extends Component {
  #abort;
  #observer;
  #heroObserver;
  #mutations;
  #resizeObserver;
  #frame = 0;
  #seen = new WeakSet();
  #headings = new Map();
  #animations = new Set();
  #media = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.#observer.unobserve(entry.target);
        this.#reveal(entry.target);
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -24px 0px' });
    this.#heroObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        entry.target.toggleAttribute('data-bv-in-view', entry.isIntersecting);
      }
    });
    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const state = this.#headings.get(entry.target);
        if (state && Math.abs(state.width - entry.contentRect.width) > 2) {
          this.#splitLines(entry.target, state);
        }
      }
    });
    this.#media.addEventListener('change', () => {
      if (this.#media.matches) {
        for (const animation of this.#animations) animation.cancel();
      }
    }, { signal });
    document.addEventListener('shopify:section:load', this.#schedule, { signal });
    document.addEventListener('shopify:block:select', (event) => {
      for (const animation of this.#animations) animation.finish();
    }, { signal });
    this.#mutations = new MutationObserver((records) => {
      if (records.some(record => [...record.addedNodes].some(node => node instanceof Element && node.matches('.shopify-section, product-card, .resource-list__item, .section-resource-list')))) this.#schedule();
    });
    const main = document.getElementById('MainContent');
    if (main) this.#mutations.observe(main, { childList: true, subtree: true });
    this.#initialize();
    document.fonts.ready.then(() => {
      if (!this.isConnected) return;
      for (const [heading, state] of this.#headings) this.#splitLines(heading, state);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#heroObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#mutations?.disconnect();
    cancelAnimationFrame(this.#frame);
    for (const animation of this.#animations) animation.cancel();
    for (const [heading, state] of this.#headings) {
      heading.replaceChildren(state.original.cloneNode(true));
      if (state.label === null) heading.removeAttribute('aria-label');
      else heading.setAttribute('aria-label', state.label);
    }
    this.#headings.clear();
    this.#seen = new WeakSet();
  }

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#initialize();
    });
  };

  #initialize() {
    const main = document.getElementById('MainContent');
    if (!main) return;
    for (const [heading] of this.#headings) {
      if (!heading.isConnected) {
        this.#resizeObserver.unobserve(heading);
        this.#headings.delete(heading);
      }
    }
    if (this.dataset.hero === 'true' && main.dataset.template === 'index') {
      const hero = main.querySelector('.hero');
      if (hero && !hero.hasAttribute('data-bv-hero-motion')) {
        hero.setAttribute('data-bv-hero-motion', '');
        this.#heroObserver.observe(hero);
      }
    }
    if (this.dataset.buttons === 'true') {
      for (const button of main.querySelectorAll('a.button, a.button-secondary, button.add-to-cart-button')) {
        button.setAttribute('data-bv-button-sweep', '');
      }
    }
    if (this.dataset.text === 'true' || this.dataset.lines === 'true') {
      const selector = '#MainContent h2, #MainContent h3, #MainContent .text-block:not(:has(h1, h2, h3))';
      for (const heading of main.querySelectorAll(selector)) {
        if (this.#seen.has(heading)) continue;
        this.#observe(heading);
      }
      for (const text of main.querySelectorAll('[id$="__bv_about_me"] .text-block:not(:has(h2))')) {
        this.#observe(text);
      }
    }
  }

  #splitLines(heading, state) {
    heading.replaceChildren(state.original.cloneNode(true));
    const text = heading.textContent.trim();
    if (heading.querySelector('a, br, strong, em')) return;
    const fragment = document.createDocumentFragment();
    const words = text.split(/\s+/).map((word, index, all) => {
      const span = document.createElement('span');
      span.textContent = word;
      fragment.append(span);
      if (index < all.length - 1) fragment.append(document.createTextNode(' '));
      return span;
    });
    heading.replaceChildren(fragment);
    const groups = [];
    for (const word of words) {
      const top = Math.round(word.getBoundingClientRect().top);
      const last = groups[groups.length - 1];
      if (!last || Math.abs(last.top - top) > 3) groups.push({ top, words: [word.textContent] });
      else last.words.push(word.textContent);
    }
    const lines = document.createDocumentFragment();
    for (const group of groups) {
      const mask = document.createElement('span');
      mask.className = 'bv-motion-line';
      mask.setAttribute('aria-hidden', 'true');
      const inner = document.createElement('span');
      inner.className = 'bv-motion-line__text';
      inner.textContent = group.words.join(' ');
      mask.append(inner);
      lines.append(mask);
    }
    heading.setAttribute('aria-label', text);
    heading.replaceChildren(lines);
    state.width = heading.getBoundingClientRect().width;
  }

  #observe(element) {
    if (this.#seen.has(element)) return;
    this.#seen.add(element);
    if (element.getBoundingClientRect().top < innerHeight || element.querySelector('img, video, media-gallery')) return;
    element.dataset.bvReveal = 'ready';
    this.#observer.observe(element);
  }

  #reveal(element) {
    element.dataset.bvReveal = 'complete';
    if (this.#media.matches || window.Shopify?.designMode) return;
    const lines = element.querySelectorAll('.bv-motion-line__text');
    const targets = lines.length ? [...lines] : [element];
    targets.forEach((target, index) => {
      const animation = target.animate([
        { opacity: 0.72, transform: 'translateY(16px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 650, delay: lines.length ? index * 100 : Number(element.dataset.bvDelay || 0), easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards' });
      this.#animations.add(animation);
      animation.finished.catch(() => {}).finally(() => this.#animations.delete(animation));
    });
  }
}

class BVWatchStory extends Component {
  #observer;
  #abort;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.motion !== 'true') return;
    this.#abort = new AbortController();
    this.setAttribute('data-enhanced', '');
    this.#observer = new IntersectionObserver(() => {
      let nearest;
      let distance = Infinity;
      for (const chapter of this.querySelectorAll('[data-chapter]')) {
        const rect = chapter.getBoundingClientRect();
        const nextDistance = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
        if (nextDistance < distance) {
          nearest = chapter;
          distance = nextDistance;
        }
      }
      if (nearest) this.#activate(nearest.dataset.chapter);
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    for (const chapter of this.querySelectorAll('[data-chapter]')) this.#observer.observe(chapter);
    document.addEventListener('shopify:block:select', (event) => {
      const chapter = event.target.closest('[data-chapter]');
      if (chapter && this.contains(chapter)) this.#activate(chapter.dataset.chapter);
    }, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#abort?.abort();
  }

  #activate(index) {
    this.dataset.activeChapter = index;
    for (const chapter of this.querySelectorAll('[data-chapter]')) {
      chapter.toggleAttribute('data-active', chapter.dataset.chapter === index);
    }
    for (const image of this.querySelectorAll('[data-story-image]')) {
      image.toggleAttribute('data-active', image.dataset.storyImage === index);
    }
  }
}

if (!customElements.get('bv-premium-motion')) customElements.define('bv-premium-motion', BVPremiumMotion);
if (!customElements.get('bv-watch-story')) customElements.define('bv-watch-story', BVWatchStory);

